<template>
	<v-card v-if="pageEnabled" class="d-flex flex-column pb-5">
		<v-toolbar color="primary" class="pr-3" flat>
			<v-toolbar-title style="flex: 0 1 auto">Activity Metrics</v-toolbar-title>
			<v-spacer />

			<v-btn
				variant="flat"
				color="white"
				class="text-primary"
				prepend-icon="mdi-download"
				:disabled="exportBusy"
				title="Download the table currently on screen as CSV"
				@click="exportCsv"
			>
				Export CSV
			</v-btn>
		</v-toolbar>

		<activity-filters
			v-model="filters"
			:user-options="userOptions"
			:project-options="projectOptions"
			@reset="resetFilters"
		/>

		<five-metrics-view
			ref="metricsView"
			:filters="filters"
			:filter-user-email="filterUserEmail"
			:filter-project-name="filterProjectName"
			@open-timeline="openTimeline"
			@export-busy="exportBusy = $event"
		/>

		<timeline-dialog
			v-model="timelineOpen"
			:data-entry-uuid="timelineDataEntryUuid"
			:user-uuid="timelineUserUuid"
			:paper-name="timelinePaperName"
			:user-email="timelineUserEmail"
		/>
	</v-card>
</template>

<script>
import fileDownload from "js-file-download";
import { useLDFlag } from "launchdarkly-vue-client-sdk";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";

import ActivityFilters from "@/pages/ActivityMetrics/ActivityFilters";
import FiveMetricsView from "@/pages/ActivityMetrics/FiveMetricsView";
import TimelineDialog from "@/pages/ActivityMetrics/TimelineDialog";
import { defaultDateRange } from "@/pages/ActivityMetrics/constants";
// Two dialogs are parked (component files stay for an easy revival), both
// judged more detail than this page's audience needs (2026-08-17):
// SdeMetricsDialog (the full per-figure/per-table time breakdown) and
// EventDrillDownDialog (the raw event log, whose role the session timeline
// dialog took over).

// Filters open on (and Reset restores) the default date window. The first
// date filter was removed 2026-08-17 because bare-date params resolve
// against UTC on the server, so a range ending "today" dropped everything
// after 5pm PT; this revival is timezone-correct - the pickers hold local
// calendar days and the API gets exact day-boundary instants (constants.js).
function emptyFilters() {
	return {
		userUuid: null,
		projectUuid: null,
		...defaultDateRange(),
	};
}

// RFC-4180 escape for ONE field: quote when the text carries a comma, quote,
// or newline; double embedded quotes. null/undefined export as empty cells
// (the tables display "-", but CSV consumers do arithmetic).
function csvEscape(value) {
	if (value === null || value === undefined) {
		return "";
	}
	const text = String(value);
	if (/[",\n\r]/.test(text)) {
		return `"${text.replace(/"/g, '""')}"`;
	}
	return text;
}

// One generic serializer for every exported view: the header titles become
// the column row, each item is read by the headers' keys, same order the
// table renders. Values are the raw underlying numbers, never the display
// rounding.
function buildCsv(headers, rows) {
	const lines = [headers.map((header) => csvEscape(header.title)).join(",")];
	for (const row of rows) {
		lines.push(headers.map((header) => csvEscape(row[header.key])).join(","));
	}
	return lines.join("\n") + "\n";
}

export default {
	name: "ActivityMetrics",

	components: {
		ActivityFilters,
		FiveMetricsView,
		TimelineDialog,
	},

	setup() {
		// Release gate (metrics gathering ships before this view page): the
		// flag defaults FALSE when the LD environment does not define it, so
		// the page fails closed. The nav entries in DactylHome gate on the
		// same flag.
		const pageEnabled = useLDFlag("user-activity-admin-page", false);
		return { pageEnabled };
	},

	data() {
		return {
			filters: emptyFilters(),
			// True while FiveMetricsView has a spinner up (emitted by it), so
			// the export button never fires against a half-loaded table.
			exportBusy: false,
			userOptions: [],
			projectOptions: [],
			timelineOpen: false,
			timelineDataEntryUuid: null,
			timelineUserUuid: null,
			timelinePaperName: "",
			timelineUserEmail: "",
		};
	},

	computed: {
		// FiveMetricsView stamps active filters into export filenames but only
		// holds uuids; the page resolves display names from its option lists.
		filterUserEmail() {
			if (!this.filters.userUuid) {
				return "";
			}
			const user = this.userOptions.find(
				(option) => option.uuid === this.filters.userUuid,
			);
			return user?.email || "";
		},

		filterProjectName() {
			if (!this.filters.projectUuid) {
				return "";
			}
			const project = this.projectOptions.find(
				(option) => option.uuid === this.filters.projectUuid,
			);
			return project?.name || "";
		},
	},

	watch: {
		// Direct URL hits (and live flag flips) bounce off the page the same
		// way a missing permission would.
		pageEnabled: {
			immediate: true,
			handler(enabled) {
				if (!enabled) {
					this.$router.replace("/access-denied");
				}
			},
		},
	},

	mounted() {
		this.getUsers();
		this.getProjects();
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),

		getUsers() {
			this.$http
				// excludeAdmin scopes /users to holders of an extraction role
				// (the server subquery reads extractionRoleSlugs - curator or
				// reviewer, so an admin who also curates still appears). The
				// metrics endpoints now exclude everyone else's events, so
				// without this the picker would offer names whose every
				// selection returns an empty table.
				.get("/users", {
					params: {
						omitDeleted: true,
						itemsPerPage: -1,
						excludeAdmin: true,
					},
				})
				.then((response) => {
					this.userOptions = response.data.users?.data || [];
				})
				.catch(() => {
					// A missing user list only costs the filter dropdown, so the
					// page still works; no need to interrupt with a notification.
					this.userOptions = [];
				});
		},

		getProjects() {
			this.$http
				.get("/projects", {
					// /projects rejects sortBy without a paired sortDesc (400), so
					// the two always travel together.
					params: {
						omitDeleted: true,
						itemsPerPage: -1,
						sortBy: ["name"],
						sortDesc: [false],
					},
				})
				.then((response) => {
					this.projectOptions = response.data.projects?.data || [];
				})
				.catch(() => {
					this.projectOptions = [];
				});
		},

		resetFilters() {
			this.filters = emptyFilters();
		},

		// From a drill-in SDE row: the session timeline for that (user, SDE)
		// pair, whole history like the other drill-ins.
		openTimeline({ userUuid, userEmail, dataEntryUuid, paperName }) {
			this.timelineDataEntryUuid = dataEntryUuid;
			this.timelineUserUuid = userUuid || null;
			this.timelinePaperName = paperName || "";
			this.timelineUserEmail = userEmail || "";
			this.timelineOpen = true;
		},

		// Client-side export of the table currently on screen: FiveMetricsView
		// hands back the exact headers and rows its active v-data-table
		// renders (all rows, not the visible page), so the CSV can never
		// drift from the display. No server round-trip.
		exportCsv() {
			const view = this.$refs.metricsView?.exportView();
			if (!view) {
				this.showNotification({
					message: "Nothing to export yet.",
					status: "warning",
				});
				return;
			}
			fileDownload(buildCsv(view.headers, view.rows), view.filename);
			this.showNotification({
				message: `Exported ${view.rows.length} rows.`,
				status: "success",
			});
		},
	},
};
</script>
